// WS8 WS-5 — typed transport for the TPS (Third-Party Servicer) registry +
// per-loan assignment endpoint. Mirrors the per-provider API shape in
// @/api/serviceProviders. Mutation hooks live in
// @/hooks/portfolioAdmin/useTps.

import { api } from "../client";
import type { PagedResult } from "@/types/api";
import type { LoanServicingFeeConfigDto } from "./servicingApi";
import type {
  LinkedPortalAccountDto,
  TpsConnectOnboardingResultDto,
  TpsConnectStatusDto,
  TpsCreate,
  TpsDto,
  TpsListQuery,
  TpsOnboardingEmailResultDto,
  TpsPortalAccountDto,
  TpsReadinessSummaryDto,
  TpsSetupDto,
  TpsUpdate,
} from "@/types/tps";

export const tpsApi = {
  list: (query?: TpsListQuery) =>
    api.get<PagedResult<TpsDto>>("/portfolio-admin/tps", query),
  get: (id: string) => api.get<TpsDto>(`/portfolio-admin/tps/${id}`),
  create: (body: TpsCreate) =>
    api.post<TpsDto>("/portfolio-admin/tps", body),
  update: (id: string, body: TpsUpdate) =>
    api.put<TpsDto>(`/portfolio-admin/tps/${id}`, body),
  delete: (id: string) => api.delete<void>(`/portfolio-admin/tps/${id}`),
  setDefault: (id: string) =>
    api.post<TpsDto>(`/portfolio-admin/tps/${id}/set-default`),
  // SP-3 — setup checklist (server-derived steps) + batched picker readiness.
  setup: (id: string) =>
    api.get<TpsSetupDto>(`/portfolio-admin/tps/${id}/setup`),
  readiness: () =>
    api.get<TpsReadinessSummaryDto[]>("/portfolio-admin/tps/readiness"),
  // SP-3 — setup-checklist lifecycle actions (pre-existing endpoints that
  // never had a staff UI). certify gates Portfolio.CertifyTps (ADMIN-only);
  // connect* gate Portfolio.ManageTpsStripe; portalInvite gates Tps.Manage.
  certify: (id: string, engagementAgreementDocumentId?: string | null) =>
    api.post<TpsDto>(`/portfolio-admin/tps/${id}/certify`, {
      engagementAgreementDocumentId: engagementAgreementDocumentId ?? null,
    }),
  portalInvite: (id: string) =>
    api.post<TpsPortalAccountDto>(`/portfolio-admin/tps/${id}/portal-invite`),
  // Portal absorption (WS-D2) — the thin reverse LinkedTpsId lookup behind
  // the shared staff PortalAccountCard / AccountNotificationsPanel.
  portalAccount: (id: string) =>
    api.get<LinkedPortalAccountDto>(`/portfolio-admin/tps/${id}/portal-account`),
  connectProvision: (id: string) =>
    api.post<TpsConnectOnboardingResultDto>(
      `/portfolio-admin/tps/${id}/connect/provision`,
    ),
  connectStatus: (id: string) =>
    api.get<TpsConnectStatusDto>(`/portfolio-admin/tps/${id}/connect/status`),
  // Section 2 (#7a) — email the firm its onboarding link(s) instead of copying
  // raw links by hand (gates Portfolio.ManageTpsStripe).
  connectEmailOnboardingLink: (id: string) =>
    api.post<TpsOnboardingEmailResultDto>(
      `/portfolio-admin/tps/${id}/connect/email-onboarding-link`,
    ),
};

/** Reassigns a loan's TPS. Owned by WS-3 on the backend. */
export function reassignTpsForLoan(loanId: string, tpsId: string) {
  return api.patch<TpsDto>(
    `/portfolio-admin/loans/${loanId}/tps-assignment`,
    { tpsId },
  );
}

/** SP-3 — explicitly re-stamps the loan's servicing-fee config from its
 * current TPS's typed MonthlyServicingFee schedule row (the deliberate
 * alternative to auto-propagating TPS schedule edits). */
export function restampTpsFeeForLoan(loanId: string) {
  return api.post<LoanServicingFeeConfigDto>(
    `/portfolio-admin/loans/${loanId}/tps-assignment/restamp-fee`,
  );
}
